import { alpha, type SxProps, type Theme } from '@mui/material';

const ACTION_BUTTON_RADIUS = '100px';
const ROUND_RADIUS = 999;

type TDividerVariant = 'none' | 'full' | 'inset';

type TRowStyleOptions = {
  isDocumentRow: boolean;
  isReadOnly: boolean;
  isExpandable: boolean;
  dividerVariant: TDividerVariant;
};

const createRowDividerStyles = (theme: Theme, dividerVariant: TDividerVariant) => ({
  '&::after': {
    content: '""',
    position: 'absolute',
    display: dividerVariant === 'none' ? 'none' : 'block',
    left: dividerVariant === 'inset' ? theme.spacing(4) : 0,
    right: dividerVariant === 'inset' ? theme.spacing(4) : 0,
    bottom: 0,
    height: '1px',
    backgroundColor: theme.palette.border.subtle,
    pointerEvents: 'none',
    zIndex: 2,
  },
});

export const appFormStyles = {
  pageRoot: (theme: Theme) => ({
    minHeight: '100vh',
    background: theme.palette.background.default,
    paddingTop: { xs: theme.spacing(2), md: theme.spacing(4) },
    paddingBottom: { xs: theme.spacing(3), md: theme.spacing(6) },
    fontFamily: '"Nunito Sans", sans-serif',
  }),
  paper: {
    p: 0,
    borderRadius: 0,
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
  } as SxProps<Theme>,
  header: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.25),
    mb: theme.spacing(1.5),
  }),
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
  } as SxProps<Theme>,
  title: (theme: Theme) => ({
    color: theme.palette.text.primary,
    fontSize: '32px',
    fontWeight: 800,
    lineHeight: '100%',
    letterSpacing: 0,
    mb: 0,
  }),
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 1,
  } as SxProps<Theme>,
  metaRow: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'flex-end' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: theme.spacing(1),
  }),
  createdAt: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: '20px',
    whiteSpace: { xs: 'normal', md: 'nowrap' },
    alignSelf: { xs: 'flex-start', sm: 'flex-end' },
  }),
  signatureNotice: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    width: 'calc(100% - 2px)',
    boxSizing: 'border-box',
    gap: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.text.primary,
    px: { xs: theme.spacing(2), sm: theme.spacing(3) },
    py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    mb: theme.spacing(1.5),
  }),
  signatureNoticeIcon: (theme: Theme) => ({
    flexShrink: 0,
    width: 36,
    height: 36,
    color: theme.palette.warning.main,
  }),
  signatureNoticeContent: {
    minWidth: 0,
  } as SxProps<Theme>,
  signatureNoticeTitle: (theme: Theme) => ({
    display: 'block',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 800,
    mb: theme.spacing(1.25),
  }),
  signatureNoticeInstruction: {
    display: 'block',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 400,
  } as SxProps<Theme>,
  signatureNoticeAllowedTypes: (theme: Theme) => ({
    display: 'block',
    mt: theme.spacing(1),
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 400,
  }),
  signatureNoticeMenuIcon: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 19,
    height: 19,
    borderRadius: ROUND_RADIUS,
    border: `2px solid ${theme.palette.common.black}`,
    color: theme.palette.common.black,
    mx: theme.spacing(0.35),
    lineHeight: 0,
    verticalAlign: 'text-bottom',
  }),
  signatureNoticeMenuDots: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1.5px',
    width: 8,
    height: 12,
  } as SxProps<Theme>,
  signatureNoticeMenuDot: {
    width: 2.5,
    height: 2.5,
    borderRadius: ROUND_RADIUS,
    backgroundColor: 'currentColor',
    flexShrink: 0,
  } as SxProps<Theme>,
  docsPaper: {
    p: 0,
    overflow: 'visible',
    borderRadius: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  } as SxProps<Theme>,
  docsList: (theme: Theme) => ({
    width: '100%',
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.border.subtle}`,
    overflow: 'hidden',
  }),
  docsTable: (theme: Theme) => ({
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.text.secondary, 0.28),
      borderRadius: ROUND_RADIUS,
    },
  }),
  table: {
    width: '100%',
    minWidth: { xs: 760, md: '100%' },
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0,
  } as SxProps<Theme>,
  docsHead: (theme: Theme) => ({
    backgroundColor: theme.palette.background.tableHeader,
  }),
  docsBody: {
    '& .MuiTableCell-root': {
      borderBottom: '0 !important',
      boxShadow: 'none',
    },
  } as SxProps<Theme>,
  docsHeaderCell: (theme: Theme) => ({
    color: theme.palette.text.primary,
    py: theme.spacing(1.75),
    backgroundColor: theme.palette.background.tableHeader,
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 800,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: 0,
    whiteSpace: 'nowrap',
  }),
  docsEmptyCell: (theme: Theme) => ({
    py: theme.spacing(6),
    backgroundColor: theme.palette.common.white,
  }),
  docsPagination: {
    backgroundColor: 'transparent',
    borderTop: 'none',
    borderRadius: 0,
  } as SxProps<Theme>,
  row:
    ({ isDocumentRow, isReadOnly, isExpandable, dividerVariant }: TRowStyleOptions) =>
    (theme: Theme) => ({
      position: 'relative',
      cursor: isExpandable ? 'pointer' : 'default',
      backgroundColor: theme.palette.common.white,
      transition: 'none',
      '& td, & th': {
        borderBottom: '0 !important',
        backgroundColor: theme.palette.common.white,
        py: theme.spacing(1.75),
        transition: isDocumentRow ? 'font-size 0.18s ease, font-weight 0.18s ease' : 'none',
      },
      '&:hover': {
        backgroundColor: theme.palette.common.white,
      },
      '&:hover td, &:hover th': {
        backgroundColor: theme.palette.common.white,
      },
      ...(isDocumentRow && {
        '&:hover .document-row-name': {
          fontSize: '17px',
          fontWeight: 800,
        },
      }),
      ...(isReadOnly &&
        !isDocumentRow && {
          '& td, & th': {
            color: theme.palette.text.primary,
          },
        }),
      ...createRowDividerStyles(theme, dividerVariant),
    }),
  nameCell: (isDocumentRow: boolean) => (theme: Theme) => ({
    pl: isDocumentRow ? theme.spacing(2) : theme.spacing(3),
    pr: theme.spacing(2),
    width: '35%',
    minWidth: 290,
    verticalAlign: 'middle',
  }),
  nameContent: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 1,
  } as SxProps<Theme>,
  documentToggleWrap: {
    width: 20,
    height: 20,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as SxProps<Theme>,
  expandButton: (theme: Theme) => ({
    width: 20,
    height: 20,
    p: 0,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary['200'],
    },
  }),
  expandButtonPlaceholder: {
    width: 20,
    height: 20,
    flexShrink: 0,
  } as SxProps<Theme>,
  documentName: {
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: '20px',
    color: 'text.primary',
    wordBreak: 'break-word',
    transition: 'font-size 0.18s ease, font-weight 0.18s ease',
  } as SxProps<Theme>,
  fileNameWrap: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 1,
  } as SxProps<Theme>,
  fileIcon: {
    width: 18,
    height: 18,
    flexShrink: 0,
    color: 'text.secondary',
  } as SxProps<Theme>,
  fileName: {
    minWidth: 0,
    color: 'text.primary',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  } as SxProps<Theme>,
  commentCell: (theme: Theme) => ({
    width: '19%',
    minWidth: 188,
    pr: theme.spacing(2),
    verticalAlign: 'middle',
  }),
  dataCell: (theme: Theme) => ({
    width: '14%',
    minWidth: 120,
    pr: theme.spacing(2),
    verticalAlign: 'middle',
  }),
  periodText: {
    color: 'text.primary',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,
  deadlineText: {
    color: 'text.primary',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,
  statusCell: (theme: Theme) => ({
    width: '14%',
    minWidth: 130,
    height: 64,
    pr: theme.spacing(2),
    verticalAlign: 'middle',
  }),
  actionsCell: (theme: Theme) => ({
    position: 'relative',
    width: '15%',
    minWidth: 156,
    height: 64,
    pl: theme.spacing(0.5),
    pr: theme.spacing(0.5),
    verticalAlign: 'middle',
  }),
  actionsWrap: (isDocumentRow: boolean) => (theme: Theme) =>
    isDocumentRow
      ? {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minWidth: 0,
          width: '100%',
        }
      : {
          position: 'absolute',
          top: '50%',
          right: theme.spacing(4),
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 28,
          width: 28,
          height: 28,
        },
  mainActionButton: (theme: Theme) => ({
    minHeight: 36,
    minWidth: 134,
    width: 134,
    borderRadius: ROUND_RADIUS,
    borderColor: theme.palette.border.subtle,
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexWrap: 'nowrap',
    paddingLeft: theme.spacing(2.75),
    paddingRight: theme.spacing(2.25),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  }),
  covenantActionButton: (theme: Theme) => ({
    minHeight: 32,
    minWidth: 176,
    width: 176,
    borderRadius: ACTION_BUTTON_RADIUS,
    borderColor: theme.palette.border.subtle,
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    paddingLeft: theme.spacing(2.25),
    paddingRight: theme.spacing(2.25),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  }),
  fileActionMenuButton: (isOpen: boolean) => (theme: Theme) => ({
    width: 28,
    height: 28,
    minWidth: 28,
    p: 0,
    borderRadius: ROUND_RADIUS,
    border: `2px solid ${isOpen ? theme.palette.primary.main : theme.palette.common.black}`,
    color: isOpen ? theme.palette.primary.main : theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  }),
  fileActionMenuIcon: {
    fontSize: 20,
  } as SxProps<Theme>,
  fileActionMenuDots: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    width: 10,
    height: 16,
  } as SxProps<Theme>,
  fileActionMenuDot: {
    width: 3,
    height: 3,
    borderRadius: ROUND_RADIUS,
    backgroundColor: 'currentColor',
    flexShrink: 0,
  } as SxProps<Theme>,
  actionMenuPaper: (theme: Theme) => ({
    minWidth: 196,
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1.25),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.palette.shadow.menu,
    marginTop: theme.spacing(0.5),
    '& .MuiMenu-list': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiMenuItem-root': {
      backgroundColor: theme.palette.common.white,
    },
  }),
  actionMenuItem: {
    minHeight: 40,
    backgroundColor: 'common.white',
    fontSize: '0.875rem',
    color: 'text.primary',
    gap: 1.5,
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
    },
  } as SxProps<Theme>,
  actionMenuItemDanger: {
    minHeight: 40,
    backgroundColor: 'common.white',
    fontSize: '0.875rem',
    color: 'error.main',
    gap: 1.5,
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
    },
  } as SxProps<Theme>,
  actionMenuItemIcon: {
    minWidth: 20,
    justifyContent: 'flex-end',
    color: 'text.primary',
  } as SxProps<Theme>,
  actionMenuItemDangerIcon: {
    minWidth: 20,
    justifyContent: 'flex-end',
    color: 'error.main',
  } as SxProps<Theme>,
  documentGenerationDialogPaper: (theme: Theme) => ({
    width: 'calc(100vw - 16px)',
    maxWidth: 1920,
    height: 'calc(100vh - 64px)',
    maxHeight: 'calc(100vh - 64px)',
    margin: `${theme.spacing(4)} ${theme.spacing(1)}`,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  }),
  documentGenerationDialogTitle: (theme: Theme) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
  }),
  documentGenerationDialogTitleText: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    paddingLeft: 48,
    paddingRight: 48,
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 800,
    fontSize: '24px',
    lineHeight: '24px',
    letterSpacing: 0,
  } as SxProps<Theme>,
  documentGenerationDialogCloseButton: (theme: Theme) => ({
    position: 'absolute',
    top: '50%',
    right: theme.spacing(2),
    transform: 'translateY(-50%)',
    color: theme.palette.text.primary,
  }),
  documentGenerationDialogContent: {
    flex: '1 1 auto',
    overflowY: 'auto',
    px: { xs: 1.5, sm: 2 },
  } as SxProps<Theme>,
  documentGenerationDialogActions: (theme: Theme) => ({
    justifyContent: 'center',
    px: theme.spacing(3),
    pt: theme.spacing(2.5),
    pb: theme.spacing(3),
  }),
  footer: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    mt: theme.spacing(4),
    gap: theme.spacing(2),
    pb: { xs: theme.spacing(1), md: 0 },
  }),
  submit: (theme: Theme) => ({
    minHeight: 44,
    width: { xs: '100%', sm: 'auto' },
    px: theme.spacing(4),
    fontWeight: 'bold',
    borderRadius: theme.spacing(1),
  }),
  requiredSignBtn: (theme: Theme) => ({
    textTransform: 'none',
    fontSize: '0.75rem',
    lineHeight: 1.2,
    height: 36,
    fontWeight: 'bold',
    borderRadius: theme.spacing(1),
    whiteSpace: 'nowrap',
    width: { xs: '100%', sm: 'auto' },
    mt: { xs: theme.spacing(1), sm: 0 },
  }),
  requiredPendingBtn: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
  } as SxProps<Theme>,
} as const;
